import { useEffect } from "react";
import { getMetaData } from "../../utils/Constant";

const JPGFormatGuide = () => {
  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("jpg-meta-data");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description;
  }, []);

  return (
    <div className="container mt-5 mb-5">
      <article className="bg-white rounded-4 shadow-lg border-0 overflow-hidden">
        {/* Header Section */}
        <header
          className="text-center py-5 px-3 position-relative"
          style={{
            background: "linear-gradient(135deg, #198754 0%, #20c997 100%)",
            color: "#fff",
          }}
        >
          <h1 className="display-5 fw-bold mb-3 position-relative">
          When to Use JPG: Pros, Cons, and Use Cases
          </h1>
          <p className="lead mb-0 position-relative fs-4">
            When to embrace them, when to avoid them, and how to get the most
            out of this classic format
          </p>
          <div className="mt-4 position-relative">
            <span className="badge bg-light text-success fs-6 me-2 p-2">
              Photography
            </span>
            <span className="badge bg-light text-success fs-6 me-2 p-2">
              Web Performance
            </span>
            <span className="badge bg-light text-success fs-6 p-2">
              File Formats
            </span>
          </div>
        </header>

        {/* Content Section */}
        <section className="p-4 p-md-5">
          {/* Introduction */}
          <div
            className="alert alert-success border-0 rounded-3 shadow-sm"
            role="alert"
          >
            <div className="d-flex">
              <div className="me-3 fs-4">📸</div>
              <div>
                <p className="fs-5 lh-lg mb-0">
                  JPG has been the workhorse of digital imaging for decades, but
                  I still see people using it wrong every day. Understanding the
                  nuances of compression, quality settings, and when to use
                  modern alternatives will save you countless headaches and
                  improve your website performance.
                </p>
              </div>
            </div>
          </div>

          {/* Important Disclaimer */}
          <div className="alert alert-warning border-0 rounded-3 mb-5">
            <div className="d-flex">
              <div className="me-3 fs-5">⚠️</div>
              <div>
                <strong>Important note about quality settings:</strong> The
                quality percentages mentioned in this guide (60%, 75%, 90%,
                etc.) are relative and vary between software. Photoshop, GIMP,
                and online tools all interpret these values differently. Always
                test with your specific tools and trust your eyes over the
                numbers.
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="card border-0 shadow-sm mb-5">
            <div className="card-header bg-success text-white py-3">
              <h2 className="h4 mb-0 fw-bold">What we're covering</h2>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "What JPG does brilliantly",
                      "The magic of lossy compression",
                      "Where JPG falls flat on its face",
                      "Quality settings decoded",
                      "Perfect use cases",
                      "Pro tips from the field",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 1}`}
                          className="text-decoration-none text-success hover-text-dark"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "Common JPG myths busted",
                      "Essential tools that don't suck",
                      "Performance secrets",
                      "Final thoughts",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 7}`}
                          className="text-decoration-none text-success hover-text-dark"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <div id="section-1" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-success border-bottom pb-2">
              <span
                className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                1
              </span>
              What makes JPG the go-to for photos?
            </h2>
            <p className="text-secondary fs-5">
              JPG (or JPEG) is like that reliable old truck that just gets the
              job done. It's been around since 1992, and for good reason - it
              understands how human vision works and focuses on what actually
              matters to our eyes.
            </p>
            <div className="row g-3 mt-3">
              {[
                {
                  icon: "📉",
                  text: "Efficient Compression",
                  desc: "Typically 3-10x smaller than PNG for photos, depending on content complexity",
                },
                {
                  icon: "👁️",
                  text: "Psychovisual Optimization",
                  desc: "Keeps details we notice, discards subtle variations we don't",
                },
                {
                  icon: "📷",
                  text: "Camera Standard",
                  desc: "Preserves EXIF data like shutter speed, aperture, and GPS",
                },
                {
                  icon: "🌐",
                  text: "Universal Support",
                  desc: "Opens on every device, browser, and app ever made",
                },
                {
                  icon: "🎨",
                  text: "16 Million Colors",
                  desc: "Handles complex color gradients beautifully",
                },
                {
                  icon: "⚡",
                  text: "Progressive Loading",
                  desc: "Can load blurry to sharp - great for slow connections",
                },
              ].map((item, index) => (
                <div key={index} className="col-6 col-md-4 col-lg-2">
                  <div className="p-3 border rounded-3 text-center bg-light hover-lift transition-all h-100">
                    <div className="fs-2 mb-2">{item.icon}</div>
                    <div className="small fw-medium text-dark">{item.text}</div>
                    <div className="text-muted small mt-1">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 2 */}
          <div id="section-2" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-success border-bottom pb-2">
              <span
                className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                2
              </span>
              The magic of lossy compression (and when it backfires)
            </h2>
            <div className="row g-4">
              {[
                {
                  icon: "🎯",
                  title: "Smart Compression",
                  desc: "JPG focuses on preserving the details our brains care about most in photographic content",
                },
                {
                  icon: "🏗️",
                  title: "Progressive Loading",
                  desc: "Can load a blurry version first then sharpen up - perfect for slow connections",
                },
                {
                  icon: "🎛️",
                  title: "Quality Control",
                  desc: "You decide the trade-off: smaller file vs better quality on a sliding scale",
                },
                {
                  icon: "📊",
                  title: "Metadata Rich",
                  desc: "Keeps all your camera settings, dates, and even GPS locations intact",
                },
              ].map((item, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-start p-3 border rounded-3 bg-light h-100">
                    <span className="fs-2 me-3">{item.icon}</span>
                    <div>
                      <h5 className="fw-bold mb-1">{item.title}</h5>
                      <p className="text-secondary mb-0">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="alert alert-warning mt-4" role="alert">
              <strong>Generation loss reality:</strong> While you might not
              notice quality loss in the first 2-3 re-saves, after 5+ edit-save
              cycles, the degradation becomes clearly visible. It's like making
              photocopies of photocopies.
            </div>
          </div>

          {/* Section 3 - Where JPG Falls Short */}
          <div id="section-3" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-success border-bottom pb-2">
              <span
                className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                3
              </span>
              Where JPG will let you down (hard)
            </h2>
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm">
                <thead className="table-success">
                  <tr>
                    <th className="border-0">The Weakness</th>
                    <th className="border-0">What Goes Wrong</th>
                    <th className="border-0">Better Choice</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      weakness: "Text & Logos",
                      impact:
                        "Creates ugly artifacts and blurry edges around text",
                      alternative: "PNG for sharp edges and transparency",
                    },
                    {
                      weakness: "Multiple Edits",
                      impact:
                        "Quality degrades with each save - visible after 5+ cycles",
                      alternative: "Edit originals, save final as JPG once",
                    },
                    {
                      weakness: "Solid Colors",
                      impact:
                        "Creates visible compression artifacts in flat areas",
                      alternative: "PNG for solid colors and simple graphics",
                    },
                    {
                      weakness: "Transparency",
                      impact:
                        "No transparency support at all - always has a background",
                      alternative: "PNG or WebP for transparent images",
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">{row.weakness}</td>
                      <td className="text-danger">{row.impact}</td>
                      <td>
                        <code>{row.alternative}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 4 - Quality Settings */}
          <div id="section-4" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-success border-bottom pb-2">
              <span
                className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                4
              </span>
              Quality settings decoded: What those numbers actually mean
            </h2>

            <div className="alert alert-info mb-4">
              <strong>Note:</strong> These are general guidelines. Actual
              results vary between software. Photoshop's "75" quality produces
              different results than GIMP's "75" or online tools.
            </div>

            <div className="row g-4">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-warning text-dark py-3">
                    <h5 className="mb-0 fw-bold">60-70% - Web Sweet Spot</h5>
                  </div>
                  <div className="card-body">
                    <p className="fst-italic mb-3">
                      "Good enough quality, great file size"
                    </p>
                    <ul className="text-secondary">
                      <li>
                        <strong>Perfect for websites</strong> and social media
                      </li>
                      <li>
                        <strong>Small artifacts</strong> but barely noticeable
                      </li>
                      <li>
                        <strong>70-90% smaller</strong> than original
                      </li>
                      <li>My personal default for most web images</li>
                      <li>Users won't notice quality difference</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-warning me-2">Balanced</span>
                      <span className="badge bg-warning">Web Ready</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-info text-white py-3">
                    <h5 className="mb-0 fw-bold">80-90% - High Quality</h5>
                  </div>
                  <div className="card-body">
                    <p className="fst-italic mb-3">
                      "Great quality, reasonable size"
                    </p>
                    <ul className="text-secondary">
                      <li>
                        <strong>Print and professional</strong> use
                      </li>
                      <li>
                        <strong>Nearly perfect</strong> quality to the eye
                      </li>
                      <li>
                        <strong>50-70% smaller</strong> than original
                      </li>
                      <li>Use for portfolio work</li>
                      <li>Client deliverables</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-info me-2">Pro Quality</span>
                      <span className="badge bg-info">Print Ready</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-danger text-white py-3">
                    <h5 className="mb-0 fw-bold">90-100% - Maximum</h5>
                  </div>
                  <div className="card-body">
                    <p className="fst-italic mb-3">
                      "Barely compressed, huge files"
                    </p>
                    <ul className="text-secondary">
                      <li>
                        <strong>Rarely needed</strong> for most uses
                      </li>
                      <li>
                        <strong>Minimal quality gain</strong> for huge size cost
                      </li>
                      <li>
                        <strong>20-40% smaller</strong> than original
                      </li>
                      <li>Only for archival purposes</li>
                      <li>Future editing master files</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-danger me-2">Huge Files</span>
                      <span className="badge bg-danger">Archive Only</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="alert alert-success mt-4" role="alert">
              <strong>My recommendation:</strong> Start at 75% quality and go
              down until you notice artifacts, then bump up slightly. For web,
              60-75% is usually the sweet spot where users can't tell the
              difference but your site loads much faster.
            </div>
          </div>

          {/* Section 5 - Real World Use Cases */}
          <div id="section-5" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-success border-bottom pb-2">
              <span
                className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                5
              </span>
              Real-World Use Cases: Where JPG Actually Shines
            </h2>
            <div className="row g-4">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0 fw-bold">✅ Perfect For JPG</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li>
                        <strong>Photographs</strong> - landscapes, portraits,
                        products
                      </li>
                      <li>
                        <strong>Website hero images</strong> - fast loading,
                        great quality
                      </li>
                      <li>
                        <strong>Social media posts</strong> - quick to load in
                        feeds
                      </li>
                      <li>
                        <strong>Image galleries</strong> - keeps page weight
                        down
                      </li>
                      <li>
                        <strong>Email attachments</strong> - universal
                        compatibility
                      </li>
                      <li>
                        <strong>Camera originals</strong> - preserves EXIF data
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-danger text-white py-3">
                    <h5 className="mb-0 fw-bold">❌ Avoid For JPG</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li>
                        <strong>Logos & text graphics</strong> - creates ugly
                        artifacts
                      </li>
                      <li>
                        <strong>Screenshots with text</strong> - text becomes
                        unreadable
                      </li>
                      <li>
                        <strong>Icons & UI elements</strong> - edges get blurry
                      </li>
                      <li>
                        <strong>Graphics with transparency</strong> - no support
                      </li>
                      <li>
                        <strong>Medical/scientific images</strong> - can't lose
                        details
                      </li>
                      <li>
                        <strong>Multiple edit sessions</strong> - quality
                        degrades
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-info text-white py-3">
                    <h5 className="mb-0 fw-bold">⚡ Modern Alternatives</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li>
                        <strong>WebP:</strong> 25-35% smaller than JPG, same
                        quality
                      </li>
                      <li>
                        <strong>AVIF:</strong> Next-gen, better compression but
                        slower encoding
                      </li>
                      <li>
                        <strong>HEIC:</strong> Apple's efficient format (limited
                        cross-platform)
                      </li>
                      <li>
                        <strong>PNG:</strong> When you need transparency or
                        sharp edges
                      </li>
                      <li>
                        <strong>SVG:</strong> For vector graphics (different use
                        case)
                      </li>
                    </ul>
                    <div className="mt-2 p-2 bg-light rounded">
                      <small className="text-muted">
                        <strong>Note:</strong> AVIF lacks progressive loading
                        support that JPG has. HEIC works best in Apple
                        ecosystems.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 6 - Pro Tips */}
          <div id="section-6" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-success border-bottom pb-2">
              <span
                className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                6
              </span>
              Pro Tips From The Field
            </h2>
            <div className="row g-3">
              {[
                "Always shoot in RAW+JPG if your camera supports it - RAW for editing, JPG for sharing",
                "Use 60-75% quality for web images - users can't tell the difference but your site loads faster",
                "Enable progressive JPG for large images - they appear to load faster to users",
                "Resize images before compressing - no point compressing a 4000px image to display at 800px",
                "Strip metadata for web images - reduces file size and protects privacy",
                "Use different quality settings for different content - 60% for thumbnails, 75% for main images",
              ].map((tip, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-center p-3 border rounded-3 bg-light h-100">
                    <div
                      className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: "30px", height: "30px" }}
                    >
                      💡
                    </div>
                    <span className="fw-medium">{tip}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 7 - Myth Busting */}
          <div id="section-7" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-success border-bottom pb-2">
              <span
                className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                7
              </span>
              JPG Myths That Need to Die
            </h2>
            <div
              className="alert alert-warning border-0 rounded-3"
              role="alert"
            >
              <div className="row g-3">
                {[
                  "MYTH: Higher quality always looks better → TRUTH: Above 85%, humans can't tell difference but file size balloons",
                  "MYTH: JPG is low quality → TRUTH: At proper settings, it's visually identical to PNG for photos",
                  "MYTH: You should always use 100% quality → TRUTH: This creates huge files with zero visible benefit",
                  "MYTH: JPG compression is bad → TRUTH: It's brilliant psychovisual optimization that understands human vision",
                  "MYTH: All image editors compress JPG the same → TRUTH: Photoshop, GIMP, and online tools produce different results",
                ].map((myth, index) => (
                  <div
                    key={index}
                    className="col-md-12 d-flex align-items-start mb-3"
                  >
                    <span className="text-danger me-2 fw-bold fs-5">🚫</span>
                    <span className="fs-6">{myth}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section 8 - Tools */}
          <div id="section-8" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-success border-bottom pb-2">
              <span
                className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                8
              </span>
              Essential JPG Tools That Don't Suck
            </h2>
            <div className="list-group shadow-sm">
              {[
                {
                  name: "Adobe Photoshop",
                  desc: "The gold standard. Use 'Export As' instead of 'Save As' for better compression. The quality slider actually makes sense here.",
                  badge: "Industry Standard",
                  link: "https://www.adobe.com/photoshop",
                },
                {
                  name: "Squoosh by Google",
                  desc: "Web-based and amazing for comparing different compression settings side-by-side. Perfect for finding your quality sweet spot.",
                  link: "https://squoosh.app",
                },
                {
                  name: "ImageMagick (CLI)",
                  desc: "For the command-line warriors. Batch process thousands of images with precise quality control. Steep learning curve but worth it.",
                  link: "https://imagemagick.org",
                },
                {
                  name: "GIMP",
                  desc: "Free, open-source, and surprisingly good JPG compression. The 'Export As' dialog gives you full control over quality settings.",
                  link: "https://www.gimp.org",
                },
              ].map((tool, index) => (
                <div
                  key={index}
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3"
                >
                  <div>
                    <h6 className="mb-1 fw-bold">
                      <a
                        href={tool.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none text-dark"
                      >
                        {tool.name}
                      </a>
                    </h6>
                    <p className="mb-1 text-secondary">{tool.desc}</p>
                  </div>
                  {tool.badge && (
                    <span className="badge bg-success rounded-pill">
                      {tool.badge}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Section 9 - Performance */}
          <div id="section-9" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-success border-bottom pb-2">
              <span
                className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                9
              </span>
              Performance Secrets: Don't Murder Your Site Speed
            </h2>
            <div className="row g-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">✅ Do This</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li>Use 60-75% quality for web images</li>
                      <li>Resize images to their display dimensions</li>
                      <li>Enable progressive JPG for images over 50KB</li>
                      <li>Strip metadata for web use</li>
                      <li>Use modern formats like WebP with JPG fallbacks</li>
                      <li>Implement lazy loading for below-fold images</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-danger text-white">
                    <h5 className="mb-0">❌ Avoid This</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li>Don't use 90%+ quality for web images</li>
                      <li>
                        Avoid uploading full camera resolution to websites
                      </li>
                      <li>Don't use JPG for text or logos</li>
                      <li>Avoid multiple edit-save cycles</li>
                      <li>Don't skip compression entirely</li>
                      <li>Don't use JPG for graphics with transparency</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="alert alert-info mt-4" role="alert">
              <strong>Example from my experience:</strong> In one project,
              optimizing JPG quality from 95% to 70% (combined with other image
              optimizations) contributed to page load time improvements. While
              individual results vary, proper JPG compression is a key factor in
              web performance.
            </div>
          </div>

          {/* Section 10 - FAQ */}
          {/* <div id="section-10" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-success border-bottom pb-2">
              <span
                className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                10
              </span>
              Your JPG Questions Answered
            </h2>
            
            <div className="accordion" id="jpgFAQ">
              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                    What's the best quality setting for website photos?
                  </button>
                </h3>
                <div id="faq1" className="accordion-collapse collapse" data-bs-parent="#jpgFAQ">
                  <div className="accordion-body">
                    <p>For most website use, 60-75% is the sweet spot. Start at 75% and go down until you notice artifacts, then bump up slightly.</p>
                    <p><strong>Important:</strong> Quality numbers vary between software. Test with your specific tools.</p>
                    <p><strong>60-70%:</strong> Perfect for web, small file sizes</p>
                    <p><strong>70-80%:</strong> Great for social media and portfolios</p>
                    <p><strong>80-90%:</strong> For print and professional work</p>
                    <p><strong>90-100%:</strong> Rarely needed, huge files</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                    Why does my JPG look worse every time I save it?
                  </button>
                </h3>
                <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#jpgFAQ">
                  <div className="accordion-body">
                    <p>This is called 'generation loss' and it's JPG's biggest weakness. Every time you save a JPG, it re-compresses and loses more data.</p>
                    <p><strong>The reality:</strong> You might not notice degradation in the first 2-3 saves, but after 5+ edit-save cycles, the quality loss becomes clearly visible.</p>
                    <p><strong>The fix:</strong> Always keep your original high-quality file and only export the final version once. Use lossless formats during editing.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                    Should I use JPG or PNG for my website?
                  </button>
                </h3>
                <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#jpgFAQ">
                  <div className="accordion-body">
                    <p><strong>Use JPG for:</strong> Photographs, complex images, hero images, product photos - anything with lots of colors and details.</p>
                    <p><strong>Use PNG for:</strong> Logos, icons, screenshots, graphics with text, images needing transparency - anything with sharp edges or simple colors.</p>
                    <p><strong>Simple rule:</strong> If it's a photo, use JPG. If it's a graphic, use PNG.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq4">
                    What's the difference between JPG and JPEG?
                  </button>
                </h3>
                <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#jpgFAQ">
                  <div className="accordion-body">
                    <p>Absolutely nothing! They're the exact same format.</p>
                    <p>JPG is just the 3-letter file extension used on older Windows systems that required 3-letter extensions. JPEG is the official 4-letter name. The format is technically JPEG (Joint Photographic Experts Group), but both .jpg and .jpeg extensions work identically.</p>
                    <p>Use whichever you prefer - they're interchangeable.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq5">
                    Is JPG better than the new WebP or AVIF formats?
                  </button>
                </h3>
                <div id="faq5" className="accordion-collapse collapse" data-bs-parent="#jpgFAQ">
                  <div className="accordion-body">
                    <p><strong>WebP:</strong> Creates files 25-35% smaller than JPG at same quality, but limited to modern browsers</p>
                    <p><strong>AVIF:</strong> Even better compression than WebP, but slower encoding and lacks progressive loading</p>
                    <p><strong>JPG:</strong> Universal compatibility and progressive loading support</p>
                    <p><strong>My approach:</strong> Serve modern formats to supporting browsers with JPG fallbacks. This gives you the best of both worlds.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq6">
                    Why does my JPG have weird blocky artifacts?
                  </button>
                </h3>
                <div id="faq6" className="accordion-collapse collapse" data-bs-parent="#jpgFAQ">
                  <div className="accordion-body">
                    <p>Those blocky artifacts are what happens when JPG compression is pushed too far. The compression works in 8x8 pixel blocks, and when quality is too low, those blocks become visible.</p>
                    <p><strong>Solutions:</strong></p>
                    <ul>
                      <li>Increase the quality setting (try 70% instead of 50%)</li>
                      <li>Use a better compression algorithm (Photoshop's 'Export As' is better than 'Save As')</li>
                      <li>Avoid compressing already-compressed images</li>
                      <li>If it's text or graphics, switch to PNG instead</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Final CTA */}
          <div
            className="text-center mt-5 p-5 rounded-4"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            }}
          >
            <h2 className="h1 fw-bold mb-3 text-success">
              Ready to master JPG compression?
            </h2>
            <p className="fs-5 text-secondary mb-4">
              Get our curated collection of JPG sample files and see the quality
              vs file size trade-offs for yourself.
            </p>
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
              <a
                href="https://samplefiles.dev/sample-images/sample-jpg"
                className="btn btn-success btn-lg px-5 py-3 shadow hover-lift"
                target="_blank"
                rel="noopener noreferrer"
              >
                📸 Get JPG Sample Files
              </a>
              <a
                href="#section-1"
                className="btn btn-outline-success btn-lg px-5 py-3"
              >
                📖 Read This Again
              </a>
            </div>
            <p className="mt-3 text-muted small">
              Various quality levels and sizes - perfect for testing and
              optimization.
            </p>
          </div>
        </section>
      </article>
    </div>
  );
};

export default JPGFormatGuide;

{
  /* "Your JPG questions answered", */
}
